import { useEffect, useMemo, useRef, useState } from 'react'
import { apiFetch } from '../lib/api'

const FILTERS = [
	{ id: 'tonight', label: 'Tonight' },
	{ id: 'weekend', label: 'Weekend' },
	{ id: 'vegetarian', label: 'Vegetarian' },
	{ id: 'vegan', label: 'Vegan' },
	{ id: 'open', label: 'Open seats' },
]

const TIME_ZONE_OPTIONS = [
	{ value: 'Asia/Kolkata', label: 'India Standard Time (Asia/Kolkata)' },
	{ value: 'Asia/Dubai', label: 'Gulf Standard Time (Asia/Dubai)' },
	{ value: 'Asia/Singapore', label: 'Singapore Time (Asia/Singapore)' },
	{ value: 'Europe/London', label: 'UK Time (Europe/London)' },
	{ value: 'America/New_York', label: 'US Eastern (America/New_York)' },
	{ value: 'America/Chicago', label: 'US Central (America/Chicago)' },
	{ value: 'America/Denver', label: 'US Mountain (America/Denver)' },
	{ value: 'America/Los_Angeles', label: 'US Pacific (America/Los_Angeles)' },
	{ value: 'Australia/Sydney', label: 'Australia Eastern (Australia/Sydney)' },
]

function formatDateTime(value) {
	if (!value) return 'Time TBD'
	const date = new Date(value)
	return date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function formatDateTimeWithZone(value, timeZone = 'Asia/Kolkata') {
	if (!value) return 'Time TBD'
	const date = new Date(value)
	try {
		return date.toLocaleString([], {
			weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone,
		})
	} catch {
		return formatDateTime(value)
	}
}

function formatDuration(minutes) {
	const total = Number(minutes) || 0
	if (total < 60) return `${total} min`
	const hours = Math.floor(total / 60)
	const rem = total % 60
	return rem ? `${hours}h ${rem}m` : `${hours}h`
}

function availabilityClass(color) {
	if (color === 'red') return 'bg-red-100 text-red-700 border-red-200'
	if (color === 'amber') return 'bg-amber-100 text-amber-700 border-amber-200'
	return 'bg-emerald-100 text-emerald-700 border-emerald-200'
}

const CURRENCY_SYMBOL = { INR: '₹', USD: '$', AED: 'د.إ', SGD: 'S$', GBP: '£', EUR: '€' }

function formatPrice(pricing) {
	if (!pricing || pricing.type === 'FREE') return null
	if (pricing.type === 'SPLIT') return '🤝 Split bill'
	const sym = CURRENCY_SYMBOL[pricing.currency] || pricing.currency || ''
	return `${sym}${Number(pricing.perPerson).toLocaleString()} / person`
}

function avatarSrc(member) {
	return member.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(member.name || 'Guest')}`
}

function splitLines(value) {
	return String(value || '')
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean)
}

const INITIAL_FORM = {
	tableName: '',
	dateTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16),
	timeZone: 'Asia/Kolkata',
	durationMinutes: 90,
	location: '',
	city: '',
	area: '',
	cuisineEmoji: '🍜',
	cuisineLabel: 'Asian Comfort',
	seats: 6,
	tone: 'Warm and inclusive conversation',
	vibe: 'Casual dining with thoughtful chats',
	forWho: 'Food lovers who enjoy meeting new people',
	discussionPoints: 'Favorite underrated dishes\nFood memories from childhood\nLocal dining hacks',
	guidelines: 'Be on time\nRespect everyone\nNo hard selling or promotion',
	priceType: 'FREE',
	pricePerPerson: '',
	priceCurrency: 'INR',
}

function toInputDateTime(value) {
	const date = value ? new Date(value) : new Date()
	if (Number.isNaN(date.getTime())) return ''
	const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
	return local.toISOString().slice(0, 16)
}

function eventToForm(event) {
	if (!event) return INITIAL_FORM
	return {
		tableName: event.tableName || '',
		dateTime: toInputDateTime(event.dateTime),
		timeZone: event.timeZone || 'Asia/Kolkata',
		durationMinutes: event.durationMinutes || 90,
		location: event.location || '',
		city: event.city || '',
		area: event.area || '',
		cuisineEmoji: event.cuisineEmoji || '🍽️',
		cuisineLabel: event.cuisineLabel || 'Community Table',
		seats: event.seats || 6,
		tone: event.about?.tone || '',
		vibe: event.about?.vibe || '',
		forWho: event.about?.forWho || '',
		discussionPoints: Array.isArray(event.discussionPoints) ? event.discussionPoints.join('\n') : '',
		guidelines: Array.isArray(event.guidelines) ? event.guidelines.join('\n') : '',
		priceType: event.pricing?.type || 'FREE',
		pricePerPerson: event.pricing?.perPerson ?? '',
		priceCurrency: event.pricing?.currency || 'INR',
	}
}

export default function GroupDining({ currentUser, gpsCity, socket, onLogin, onOpenProfile, onRequirePhoneVerification, sharedEventId, cityOverride }) {
	const [events, setEvents] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [activeFilters, setActiveFilters] = useState([])
	const [selectedEvent, setSelectedEvent] = useState(null)
	const [detailLoading, setDetailLoading] = useState(false)
	const [hostingOpen, setHostingOpen] = useState(false)
	const [hosting, setHosting] = useState(false)
	const [formError, setFormError] = useState('')
	const [hostForm, setHostForm] = useState(INITIAL_FORM)
	const [joiningId, setJoiningId] = useState(null)
	const [manageOpen, setManageOpen] = useState(false)
	const [managing, setManaging] = useState(false)
	const [manageError, setManageError] = useState('')
	const [manageForm, setManageForm] = useState(INITIAL_FORM)
	const [notifyMessage, setNotifyMessage] = useState('Table details were updated. Please check latest details before arriving.')
	const [groupMessages, setGroupMessages] = useState([])
	const [groupChatLoading, setGroupChatLoading] = useState(false)
	const [groupMessageText, setGroupMessageText] = useState('')
	const [sendingGroupMessage, setSendingGroupMessage] = useState(false)
	const [hostMessageText, setHostMessageText] = useState('')
	const [hostMessageTarget, setHostMessageTarget] = useState('group')
	const [sendingHostMessage, setSendingHostMessage] = useState(false)
	const [shareNotice, setShareNotice] = useState('')
	const [pendingJoinEventId, setPendingJoinEventId] = useState(null)
	const razorpayScriptLoaded = useRef(false)

	const cityName = useMemo(() => cityOverride || currentUser?.location?.city || gpsCity || '', [cityOverride, currentUser, gpsCity])

	async function loadEvents(filters = activeFilters) {
		setLoading(true)
		setError('')
		try {
			const qs = new URLSearchParams()
			if (filters.length > 0) qs.set('filters', filters.join(','))
			if (cityName) qs.set('city', cityName)
			const res = await apiFetch(`/group${qs.toString() ? `?${qs.toString()}` : ''}`)
			if (!res.ok) throw new Error('Unable to load group dining events')
			const data = await res.json()
			setEvents(Array.isArray(data.events) ? data.events : [])
		} catch (err) {
			setError(err.message || 'Unable to load group dining events right now.')
			setEvents([])
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		const timer = setTimeout(() => {
			loadEvents(activeFilters)
		}, 0)
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cityName])

	useEffect(() => {
		if (!sharedEventId) return
		openDetail(Number(sharedEventId))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sharedEventId])

	useEffect(() => {
		if (!socket || !selectedEvent?.id || !selectedEvent.isJoined) return

		socket.emit('join_group_room', selectedEvent.id)

		function handleGroupMessage(message) {
			if (message.groupEventId !== selectedEvent.id) return
			setGroupMessages((prev) => {
				if (prev.some((item) => item.id === message.id)) return prev
				return [...prev, message]
			})
		}

		function handleGroupEventUpdated(payload) {
			if (payload?.event?.id !== selectedEvent.id) return
			setSelectedEvent(payload.event)
			setEvents((prev) => prev.map((item) => (item.id === payload.event.id ? payload.event : item)))
		}

		socket.on('new_group_message', handleGroupMessage)
		socket.on('group_event_updated', handleGroupEventUpdated)

		return () => {
			socket.off('new_group_message', handleGroupMessage)
			socket.off('group_event_updated', handleGroupEventUpdated)
			socket.emit('leave_group_room', selectedEvent.id)
		}
	}, [socket, selectedEvent?.id, selectedEvent?.isJoined])

	function toggleFilter(filterId) {
		setActiveFilters((prev) => {
			const next = prev.includes(filterId)
				? prev.filter((item) => item !== filterId)
				: [...prev, filterId]
			loadEvents(next)
			return next
		})
	}

	async function openDetail(eventId) {
		setDetailLoading(true)
		setSelectedEvent((prev) => prev && prev.id === eventId ? prev : null)
		try {
			const res = await apiFetch(`/group/${eventId}`)
			if (!res.ok) throw new Error('Unable to load event detail')
			const data = await res.json()
			const event = data.event || null
			setSelectedEvent(event)
			if (event?.isJoined && currentUser) {
				loadGroupMessages(event.id)
			} else {
				setGroupMessages([])
			}
		} catch {
			const fallback = events.find((item) => item.id === eventId)
			setSelectedEvent(fallback || null)
			if (fallback?.isJoined && currentUser) {
				loadGroupMessages(fallback.id)
			}
		} finally {
			setDetailLoading(false)
		}
	}

	async function loadGroupMessages(eventId) {
		setGroupChatLoading(true)
		try {
			const res = await apiFetch(`/group/${eventId}/messages?limit=80`)
			if (!res.ok) throw new Error('Unable to load table chat')
			const data = await res.json()
			setGroupMessages(Array.isArray(data.messages) ? data.messages : [])
		} catch {
			setGroupMessages([])
		} finally {
			setGroupChatLoading(false)
		}
	}

	function loadRazorpayScript() {
		return new Promise((resolve) => {
			if (razorpayScriptLoaded.current || window.Razorpay) {
				razorpayScriptLoaded.current = true
				return resolve(true)
			}
			const script = document.createElement('script')
			script.src = 'https://checkout.razorpay.com/v1/checkout.js'
			script.onload = () => { razorpayScriptLoaded.current = true; resolve(true) }
			script.onerror = () => resolve(false)
			document.body.appendChild(script)
		})
	}

	async function joinWithPayment(eventId, event) {
		const loaded = await loadRazorpayScript()
		if (!loaded) {
			setError('Unable to load payment gateway. Please try again.')
			setJoiningId(null)
			return
		}

		let orderData
		try {
			const res = await apiFetch(`/group/${eventId}/payment/order`, { method: 'POST' })
			const data = await res.json().catch(() => ({}))
			if (!res.ok) throw new Error(data.message || 'Unable to create payment order')
			orderData = data
		} catch (err) {
			setError(err.message || 'Unable to initiate payment')
			setJoiningId(null)
			return
		}

		const options = {
			key: orderData.keyId,
			amount: orderData.amount,
			currency: orderData.currency,
			name: 'Co-Eater',
			description: `Join: ${event?.tableName || 'Group Table'}`,
			order_id: orderData.orderId,
			prefill: {
				name: currentUser?.name || '',
				email: currentUser?.email || '',
			},
			theme: { color: '#f97316' },
			handler: async (response) => {
				try {
					const verifyRes = await apiFetch(`/group/${eventId}/payment/verify`, {
						method: 'POST',
						body: JSON.stringify({
							razorpay_order_id: response.razorpay_order_id,
							razorpay_payment_id: response.razorpay_payment_id,
							razorpay_signature: response.razorpay_signature,
						}),
					})
					const verifyData = await verifyRes.json().catch(() => ({}))
					if (!verifyRes.ok) throw new Error(verifyData.message || 'Payment verification failed')
					const updated = verifyData.event
					if (updated) {
						setEvents((prev) => prev.map((item) => item.id === eventId ? updated : item))
						setSelectedEvent((prev) => prev && prev.id === eventId ? updated : prev)
						loadGroupMessages(eventId)
					}
				} catch (err) {
					setError(err.message || 'Payment verified but joining failed. Please contact support.')
				} finally {
					setJoiningId(null)
				}
			},
			modal: {
				ondismiss: () => setJoiningId(null),
			},
		}

		const rzp = new window.Razorpay(options)
		rzp.open()
	}

	async function joinEvent(eventId) {
		if (!currentUser) {
			onLogin?.()
			return
		}
		setJoiningId(eventId)
		const eventToJoin = events.find((e) => e.id === eventId) || selectedEvent
		if (eventToJoin?.pricing?.type === 'FIXED' && eventToJoin.pricing.perPerson > 0) {
			await joinWithPayment(eventId, eventToJoin)
			return
		}
		try {
			const res = await apiFetch(`/group/${eventId}/join`, { method: 'POST' })
			if (!res.ok) {
				const data = await res.json().catch(() => ({}))
				throw new Error(data.message || 'Unable to join this table')
			}
			const data = await res.json().catch(() => ({}))
			const updated = data.event
			if (updated) {
				setEvents((prev) => prev.map((item) => item.id === eventId ? updated : item))
				setSelectedEvent((prev) => prev && prev.id === eventId ? updated : prev)
				loadGroupMessages(eventId)
			} else {
				await loadEvents(activeFilters)
				if (selectedEvent?.id === eventId) await openDetail(eventId)
			}
		} catch (err) {
			setError(err.message || 'Unable to join this table right now.')
		} finally {
			setJoiningId(null)
		}
	}

	function requestJoin(eventId) {
		if (!currentUser) {
			onLogin?.()
			return
		}
		if (!currentUser.phone) {
			setPendingJoinEventId(eventId)
			onRequirePhoneVerification?.()
			return
		}
		joinEvent(eventId)
	}

	useEffect(() => {
		if (!currentUser?.phone || pendingJoinEventId == null) return
		const eventId = pendingJoinEventId
		setPendingJoinEventId(null)
		joinEvent(eventId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser?.phone, pendingJoinEventId])

	function updateForm(field, value) {
		setHostForm((prev) => ({ ...prev, [field]: value }))
	}

	function openProfile(userId, clickEvent) {
		if (clickEvent) clickEvent.stopPropagation()
		if (!userId) return
		onOpenProfile?.(userId)
	}

	function shareLinkForEvent(eventId) {
		const origin = typeof window !== 'undefined' ? window.location.origin : 'https://coeater.com'
		return `${origin}/group/${eventId}`
	}

	async function copyShareLink(eventId, clickEvent) {
		if (clickEvent) clickEvent.stopPropagation()
		if (!eventId) return
		const url = shareLinkForEvent(eventId)
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(url)
			} else {
				const temp = document.createElement('textarea')
				temp.value = url
				temp.setAttribute('readonly', 'true')
				temp.style.position = 'absolute'
				temp.style.left = '-9999px'
				document.body.appendChild(temp)
				temp.select()
				document.execCommand('copy')
				document.body.removeChild(temp)
			}
			setShareNotice('Share link copied to clipboard.')
		} catch {
			setShareNotice('Could not copy link. Please copy from browser URL.')
		}
		setTimeout(() => setShareNotice(''), 2600)
	}

	async function submitHost(event) {
		event.preventDefault()
		if (!currentUser) {
			onLogin?.()
			return
		}
		setHosting(true)
		setFormError('')
		try {
			const payload = {
				...hostForm,
				durationMinutes: Number(hostForm.durationMinutes),
				seats: Number(hostForm.seats),
				discussionPoints: splitLines(hostForm.discussionPoints),
				guidelines: splitLines(hostForm.guidelines),
				priceType: hostForm.priceType,
				pricePerPerson: hostForm.priceType === 'FIXED' ? Number(hostForm.pricePerPerson) : undefined,
				priceCurrency: hostForm.priceCurrency || 'INR',
			}
			const res = await apiFetch('/group', {
				method: 'POST',
				body: JSON.stringify(payload),
			})
			const data = await res.json().catch(() => ({}))
			if (!res.ok) throw new Error(data.message || 'Unable to host this table')

			const created = data.event
			if (created) {
				setEvents((prev) => [created, ...prev])
				setSelectedEvent(created)
			} else {
				await loadEvents(activeFilters)
			}

			setHostingOpen(false)
			setHostForm(INITIAL_FORM)
		} catch (err) {
			setFormError(err.message || 'Unable to host this table')
		} finally {
			setHosting(false)
		}
	}

	async function submitManage(event) {
		event.preventDefault()
		if (!selectedEvent) return
		setManaging(true)
		setManageError('')
		try {
			const payload = {
				...manageForm,
				durationMinutes: Number(manageForm.durationMinutes),
				seats: Number(manageForm.seats),
				discussionPoints: splitLines(manageForm.discussionPoints),
				guidelines: splitLines(manageForm.guidelines),
				notifyMessage,
				priceType: manageForm.priceType,
				pricePerPerson: manageForm.priceType === 'FIXED' ? Number(manageForm.pricePerPerson) : undefined,
				priceCurrency: manageForm.priceCurrency || 'INR',
			}
			const res = await apiFetch(`/group/${selectedEvent.id}`, {
				method: 'PATCH',
				body: JSON.stringify(payload),
			})
			const data = await res.json().catch(() => ({}))
			if (!res.ok) throw new Error(data.message || 'Unable to update this table')

			if (data.event) {
				setSelectedEvent(data.event)
				setEvents((prev) => prev.map((item) => (item.id === data.event.id ? data.event : item)))
			}
			setManageOpen(false)
		} catch (err) {
			setManageError(err.message || 'Unable to update this table')
		} finally {
			setManaging(false)
		}
	}

	async function sendGroupMessage() {
		if (!selectedEvent || !groupMessageText.trim()) return
		setSendingGroupMessage(true)
		try {
			const res = await apiFetch(`/group/${selectedEvent.id}/messages`, {
				method: 'POST',
				body: JSON.stringify({ text: groupMessageText.trim() }),
			})
			const data = await res.json().catch(() => ({}))
			if (!res.ok) throw new Error(data.message || 'Unable to send message')
			if (data.message) setGroupMessages((prev) => [...prev, data.message])
			setGroupMessageText('')
		} catch (err) {
			setError(err.message || 'Unable to send message')
		} finally {
			setSendingGroupMessage(false)
		}
	}

	async function sendHostMessage() {
		if (!selectedEvent || !hostMessageText.trim()) return
		setSendingHostMessage(true)
		try {
			const payload = { text: hostMessageText.trim() }
			if (hostMessageTarget !== 'group') payload.recipientUserId = Number(hostMessageTarget)
			const res = await apiFetch(`/group/${selectedEvent.id}/host-message`, {
				method: 'POST',
				body: JSON.stringify(payload),
			})
			const data = await res.json().catch(() => ({}))
			if (!res.ok) throw new Error(data.message || 'Unable to send host message')
			if (Array.isArray(data.messages) && data.messages.length > 0) {
				setGroupMessages((prev) => [...prev, ...data.messages])
			}
			setHostMessageText('')
		} catch (err) {
			setError(err.message || 'Unable to send host message')
		} finally {
			setSendingHostMessage(false)
		}
	}

	const showDetail = !!selectedEvent
	const isHost = !!(selectedEvent && currentUser && selectedEvent.host?.id === currentUser.id)
	const hostTargets = selectedEvent?.peopleJoining?.filter((member) => !member.isHost && member.id) || []
	const seatProgress = showDetail && selectedEvent.seats > 0
		? Math.min(100, Math.round((selectedEvent.joinedCount / selectedEvent.seats) * 100))
		: 0

	return (
		<section className="space-y-6">
			{shareNotice && (
				<div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
					{shareNotice}
				</div>
			)}
			{!showDetail && (
				<>
					<div className="fade-up rounded-3xl border border-white/70 bg-white/85 p-5 shadow-xl sm:p-6">
						<div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
							<div>
								<p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">Group Dining</p>
								<h2 className="title-font mt-1 text-2xl font-bold text-gray-900 sm:text-3xl">Find or host a shared table</h2>
								<p className="mt-2 text-sm text-gray-600">Browse live table events by vibe and cuisine, then open any card for full details.</p>
							</div>
							<div className="flex flex-wrap gap-2">
								<button
									type="button"
									onClick={() => loadEvents(activeFilters)}
									className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
								>
									Refresh
								</button>
								<button
									type="button"
									onClick={() => currentUser ? setHostingOpen(true) : onLogin?.()}
									className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-md hover:from-red-600 hover:to-orange-600"
								>
									Host a Table
								</button>
							</div>
						</div>

						<div className="mt-5 flex flex-wrap gap-2">
							{FILTERS.map((filter) => {
								const active = activeFilters.includes(filter.id)
								return (
									<button
										key={filter.id}
										type="button"
										onClick={() => toggleFilter(filter.id)}
										className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
											active
												? 'border-orange-300 bg-orange-100 text-orange-800'
												: 'border-gray-200 bg-white text-gray-600 hover:border-orange-200 hover:text-orange-700'
										}`}
									>
										{filter.label}
									</button>
								)
							})}
						</div>
					</div>

					{hostingOpen && (
						<div className="rounded-3xl border border-orange-200 bg-white p-5 shadow-xl sm:p-6">
							<div className="flex items-start justify-between gap-3">
								<div>
									<p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">Host Group Dining</p>
									<h3 className="title-font mt-1 text-xl font-bold text-gray-900">Create your table</h3>
								</div>
								<button
									type="button"
									onClick={() => { setHostingOpen(false); setFormError('') }}
									className="rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
								>
									Close
								</button>
							</div>

							<form className="mt-5 grid gap-4 md:grid-cols-2" onSubmit={submitHost}>
								<label className="text-sm font-semibold text-gray-700">Table name
									<input value={hostForm.tableName} onChange={(e) => updateForm('tableName', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Date & time
									<input type="datetime-local" value={hostForm.dateTime} onChange={(e) => updateForm('dateTime', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Time zone
									<select value={hostForm.timeZone} onChange={(e) => updateForm('timeZone', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required>
										{TIME_ZONE_OPTIONS.map((tz) => (
											<option key={tz.value} value={tz.value}>{tz.label}</option>
										))}
									</select>
								</label>
								<label className="text-sm font-semibold text-gray-700">Location
									<input value={hostForm.location} onChange={(e) => updateForm('location', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="Restaurant or meetup place" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Seats
									<input type="number" min="2" max="20" value={hostForm.seats} onChange={(e) => updateForm('seats', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Cuisine emoji
									<input value={hostForm.cuisineEmoji} onChange={(e) => updateForm('cuisineEmoji', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Cuisine label
									<input value={hostForm.cuisineLabel} onChange={(e) => updateForm('cuisineLabel', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Duration (minutes)
									<input type="number" min="30" max="300" value={hostForm.durationMinutes} onChange={(e) => updateForm('durationMinutes', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">City
									<input value={hostForm.city} onChange={(e) => updateForm('city', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Area
									<input value={hostForm.area} onChange={(e) => updateForm('area', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Tone
									<input value={hostForm.tone} onChange={(e) => updateForm('tone', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Vibe
									<input value={hostForm.vibe} onChange={(e) => updateForm('vibe', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Who it's for
									<input value={hostForm.forWho} onChange={(e) => updateForm('forWho', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700 md:col-span-2">Discussion points (one per line)
									<textarea rows={4} value={hostForm.discussionPoints} onChange={(e) => updateForm('discussionPoints', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700 md:col-span-2">Table guidelines (one per line)
									<textarea rows={3} value={hostForm.guidelines} onChange={(e) => updateForm('guidelines', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>

								{/* Pricing */}
								<div className="md:col-span-2 rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
									<p className="text-sm font-semibold text-orange-800">Table pricing</p>
									<div className="mt-3 flex flex-wrap gap-2">
										{[{id:'FREE',label:'Free'},{id:'SPLIT',label:'Split bill at table'},{id:'FIXED',label:'Fixed price per person'}].map((opt) => (
											<button
												key={opt.id}
												type="button"
												onClick={() => updateForm('priceType', opt.id)}
												className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${hostForm.priceType === opt.id ? 'border-orange-400 bg-orange-500 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'}`}
											>
												{opt.label}
											</button>
										))}
									</div>
									{hostForm.priceType === 'FIXED' && (
										<div className="mt-3 flex gap-3">
											<label className="flex-1 text-sm font-semibold text-gray-700">Amount per person
												<input type="number" min="1" step="0.01" value={hostForm.pricePerPerson} onChange={(e) => updateForm('pricePerPerson', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="e.g. 500" required />
											</label>
											<label className="w-28 text-sm font-semibold text-gray-700">Currency
												<select value={hostForm.priceCurrency} onChange={(e) => updateForm('priceCurrency', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm">
													<option value="INR">INR ₹</option>
													<option value="USD">USD $</option>
													<option value="AED">AED د.إ</option>
													<option value="SGD">SGD S$</option>
													<option value="GBP">GBP £</option>
													<option value="EUR">EUR €</option>
												</select>
											</label>
										</div>
									)}
									{hostForm.priceType === 'SPLIT' && (
										<p className="mt-2 text-xs text-orange-700">Guests will see a note that the bill will be split equally at the table. No online payment required.</p>
									)}
								</div>

								{formError && <p className="md:col-span-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}

								<div className="md:col-span-2 flex justify-end gap-2">
									<button type="button" onClick={() => setHostingOpen(false)} className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600">Cancel</button>
									<button type="submit" disabled={hosting} className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70">
										{hosting ? 'Hosting...' : 'Create Table'}
									</button>
								</div>
							</form>
						</div>
					)}

					{error && (
						<div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
							{error}
						</div>
					)}

					{loading ? (
						<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
							{Array.from({ length: 6 }).map((_, idx) => (
								<div key={idx} className="animate-pulse rounded-3xl border border-white/70 bg-white/80 p-4 shadow-lg">
									<div className="h-4 w-2/3 rounded bg-gray-200" />
									<div className="mt-3 h-3 w-full rounded bg-gray-200" />
									<div className="mt-2 h-3 w-4/5 rounded bg-gray-200" />
									<div className="mt-6 h-9 w-28 rounded-full bg-gray-200" />
								</div>
							))}
						</div>
					) : events.length === 0 ? (
						<div className="rounded-3xl border border-white/70 bg-white/85 p-10 text-center shadow-xl">
							<p className="text-4xl">🍽️</p>
							<h3 className="mt-3 text-xl font-semibold text-gray-900">No table events match your filters</h3>
							<p className="mt-1 text-sm text-gray-500">Try removing filters or host the first table in your area.</p>
						</div>
					) : (
						<div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
							{events.map((event) => {
								const openSeats = Math.max(0, event.availability?.openSeats || 0)
								const disabled = event.isJoined || event.availability?.code === 'FULL'

								return (
									<article
										key={event.id}
										className="fade-up cursor-pointer rounded-3xl border border-white/70 bg-white/85 p-4 shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
										onClick={() => openDetail(event.id)}
									>
										<div className="flex items-start justify-between gap-2">
											<div>
												<p className="text-sm font-semibold text-orange-700">{event.cuisineEmoji} {event.cuisineLabel}</p>
												<h3 className="title-font mt-1 text-lg font-bold text-gray-900">{event.tableName}</h3>
											</div>
											<span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${availabilityClass(event.availability?.color)}`}>
												{event.availability?.label || 'Open'}
											</span>
										</div>

										<div className="mt-3 space-y-1 text-sm text-gray-600">
											<p>🗓️ {formatDateTimeWithZone(event.dateTime, event.timeZone || 'Asia/Kolkata')}</p>
											<p>🌍 {event.timeZone || 'Asia/Kolkata'}</p>
											<p>📍 {event.location}</p>
										</div>

										<div className="mt-4 flex items-center justify-between gap-3">
											<div className="flex -space-x-2">
												{event.peopleJoining.slice(0, 5).map((member) => (
													<button
														key={`${event.id}-${member.id || member.name}`}
														type="button"
														onClick={(e) => openProfile(member.id, e)}
														className="h-8 w-8 rounded-full border-2 border-white object-cover transition hover:scale-105"
														title={`Open ${member.name}'s profile`}
													>
														<img
															src={avatarSrc(member)}
															alt={member.name}
															className="h-full w-full rounded-full object-cover"
														/>
													</button>
												))}
												{event.peopleJoining.length > 5 && (
													<span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-orange-100 text-xs font-semibold text-orange-700">
														+{event.peopleJoining.length - 5}
													</span>
												)}
											</div>

											<button
												type="button"
												disabled={disabled || joiningId === event.id}
												onClick={(e) => {
													e.stopPropagation()
													requestJoin(event.id)
												}}
												className={`rounded-full px-4 py-2 text-xs font-semibold ${
													disabled
														? 'cursor-not-allowed bg-gray-200 text-gray-500'
														: 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600'
												}`}
											>
												{event.isJoined
												? 'Joined'
												: joiningId === event.id
												? (event.pricing?.type === 'FIXED' ? 'Opening payment...' : 'Joining...')
												: event.pricing?.type === 'FIXED'
												? `Pay & Join`
												: 'Join'}
											</button>
											{currentUser && event.host?.id === currentUser.id && (
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation()
														setSelectedEvent(event)
														setManageForm(eventToForm(event))
														setManageError('')
														setManageOpen(true)
													}}
													className="rounded-full border border-orange-300 bg-orange-50 px-4 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-100"
												>
													Manage
												</button>
											)}
											{/* <button
												type="button"
												onClick={(e) => copyShareLink(event.id, e)}
												className="rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50"
											>
												Share link
											</button> */}
										</div>

										<p className="mt-3 text-xs text-gray-500">
											{openSeats} open seat{openSeats === 1 ? '' : 's'} • Host:{' '}
											<button
												type="button"
												onClick={(e) => openProfile(event.host?.id, e)}
												className="font-semibold text-orange-700 hover:text-orange-800"
											>
												{event.host?.name || 'Host'}
											</button>
										</p>
										{formatPrice(event.pricing) && (
											<p className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700">
												💳 {formatPrice(event.pricing)}
											</p>
										)}
									</article>
								)
							})}
						</div>
					)}

				
				</>
			)}

			{showDetail && (
				<div className="fade-up rounded-3xl border border-white/70 bg-white/90 p-5 shadow-xl sm:p-6">
					<div className="mb-4 flex flex-wrap gap-2">
						<button
							type="button"
							onClick={() => setSelectedEvent(null)}
							className="rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
						>
							← Back to all tables
						</button>
						<button
							type="button"
							onClick={(e) => copyShareLink(selectedEvent?.id, e)}
							className="rounded-full border border-orange-300 bg-orange-50 px-4 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-100"
						>
							Share link
						</button>
					</div>

					{detailLoading ? (
						<div className="animate-pulse space-y-3">
							<div className="h-8 w-1/2 rounded bg-gray-200" />
							<div className="h-4 w-4/5 rounded bg-gray-200" />
							<div className="h-44 rounded-3xl bg-gray-200" />
						</div>
					) : (
						<>
							<div className="rounded-3xl bg-gradient-to-r from-orange-500 via-red-500 to-rose-500 p-5 text-white shadow-lg sm:p-6">
								<p className="text-sm font-semibold uppercase tracking-[0.2em] opacity-90">Table Overview</p>
								<h2 className="title-font mt-1 text-2xl font-bold sm:text-3xl">{selectedEvent.tableName}</h2>
								<div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold sm:text-sm">
									<span className="rounded-full bg-white/20 px-3 py-1">🕒 {formatDateTimeWithZone(selectedEvent.dateTime, selectedEvent.timeZone || 'Asia/Kolkata')}</span>
									<span className="rounded-full bg-white/20 px-3 py-1">🌍 {selectedEvent.timeZone || 'Asia/Kolkata'}</span>
									<span className="rounded-full bg-white/20 px-3 py-1">⏱️ {formatDuration(selectedEvent.durationMinutes)}</span>
									<span className="rounded-full bg-white/20 px-3 py-1">📍 {selectedEvent.location}</span>
									{formatPrice(selectedEvent.pricing) && (
										<span className="rounded-full bg-white/30 px-3 py-1">💳 {formatPrice(selectedEvent.pricing)}</span>
									)}
								</div>
							</div>

							<div className="mt-5 grid gap-5 lg:grid-cols-[1.3fr_0.7fr]">
								<div className="space-y-5">
									<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
										<h3 className="text-lg font-bold text-gray-900">About this table</h3>
										<div className="mt-3 grid gap-3 text-sm text-gray-700 sm:grid-cols-3">
											<div className="rounded-xl bg-orange-50 p-3"><p className="text-xs font-semibold uppercase text-orange-700">Tone</p><p className="mt-1">{selectedEvent.about?.tone}</p></div>
											<div className="rounded-xl bg-amber-50 p-3"><p className="text-xs font-semibold uppercase text-amber-700">Vibe</p><p className="mt-1">{selectedEvent.about?.vibe}</p></div>
											<div className="rounded-xl bg-red-50 p-3"><p className="text-xs font-semibold uppercase text-red-700">Who it's for</p><p className="mt-1">{selectedEvent.about?.forWho}</p></div>
										</div>
									</div>

									<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
										<h3 className="text-lg font-bold text-gray-900">Discussion points</h3>
										{selectedEvent.discussionPoints?.length ? (
											<ul className="mt-3 space-y-2 text-sm text-gray-700">
												{selectedEvent.discussionPoints.map((point) => (
													<li key={point} className="rounded-xl bg-gray-50 px-3 py-2">• {point}</li>
												))}
											</ul>
										) : (
											<p className="mt-2 text-sm text-gray-500">Host will share topics at the table.</p>
										)}
									</div>

									<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
										<div className="flex items-center justify-between gap-3">
											<h3 className="text-lg font-bold text-gray-900">People joining</h3>
											<p className="text-xs font-semibold text-orange-700">Open seats: {Math.max(0, selectedEvent.availability?.openSeats || 0)}</p>
										</div>
										{selectedEvent.peopleJoining?.length ? (
											<div className="mt-3 grid gap-3 sm:grid-cols-2">
												{selectedEvent.peopleJoining.map((member) => (
													<button
														type="button"
														key={`${member.id || member.name}-${member.name}`}
														onClick={(e) => openProfile(member.id, e)}
														className="flex items-center gap-3 rounded-xl bg-gray-50 p-3 text-left transition hover:bg-gray-100"
													>
														<img src={avatarSrc(member)} alt={member.name} className="h-10 w-10 rounded-full object-cover" />
														<div className="min-w-0">
															<p className="truncate text-sm font-semibold text-gray-900">
																{member.name}
																{member.isHost ? ' (Host)' : ''}
															</p>
															<p className="truncate text-xs text-gray-500">{member.bio || 'Food explorer'}</p>
														</div>
													</button>
												))}
											</div>
										) : (
											<p className="mt-2 text-sm text-gray-500">No one has joined yet. Be the first.</p>
										)}
									</div>
								</div>

								<aside className="space-y-4">
									<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
										<p className="text-sm font-semibold text-gray-700">Seat progress</p>
										<div className="mt-2 h-2.5 overflow-hidden rounded-full bg-gray-100">
											<div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-red-500" style={{ width: `${seatProgress}%` }} />
										</div>
										<p className="mt-2 text-xs text-gray-500">{selectedEvent.joinedCount}/{selectedEvent.seats} seats filled</p>

										<button
											type="button"
											disabled={selectedEvent.isJoined || selectedEvent.availability?.code === 'FULL' || joiningId === selectedEvent.id}
											onClick={() => requestJoin(selectedEvent.id)}
											className={`mt-4 w-full rounded-xl px-4 py-2.5 text-sm font-semibold ${
												selectedEvent.isJoined || selectedEvent.availability?.code === 'FULL'
													? 'cursor-not-allowed bg-gray-200 text-gray-500'
													: 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600'
											}`}
										>
											{selectedEvent.isJoined
												? 'Already Joined'
												: joiningId === selectedEvent.id
												? (selectedEvent.pricing?.type === 'FIXED' ? 'Opening payment...' : 'Joining...')
												: selectedEvent.pricing?.type === 'FIXED'
												? `Pay & Join — ${formatPrice(selectedEvent.pricing)}`
												: 'Join this table'}
										</button>
										{!selectedEvent.isJoined && selectedEvent.pricing?.type === 'SPLIT' && (
											<p className="mt-2 text-center text-xs text-violet-600">🤝 Bill will be split equally at the table</p>
										)}
									</div>

									<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
										<p className="text-sm font-semibold text-gray-700">Map</p>
										<div className="mt-2 rounded-xl border border-dashed border-orange-200 bg-orange-50 p-4 text-center text-xs text-orange-700">
											📍 {selectedEvent.location}
											<p className="mt-1 text-orange-600/80">Map stub - precise pin available after join</p>
										</div>
									</div>

									<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
										<p className="text-sm font-semibold text-gray-700">Table guidelines</p>
										{selectedEvent.guidelines?.length ? (
											<ul className="mt-2 space-y-2 text-xs text-gray-600">
												{selectedEvent.guidelines.map((rule) => (
													<li key={rule} className="rounded-lg bg-gray-50 px-2.5 py-2">• {rule}</li>
												))}
											</ul>
										) : (
											<p className="mt-2 text-xs text-gray-500">Be respectful, inclusive, and punctual.</p>
										)}
									</div>

									{isHost && (
										<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
											<p className="text-sm font-semibold text-gray-700">Manage table</p>
											<p className="mt-1 text-xs text-gray-500">Update details and message participants from here.</p>
											<button
												type="button"
												onClick={() => {
													setManageForm(eventToForm(selectedEvent))
													setManageError('')
													setManageOpen(true)
												}}
												className="mt-3 w-full rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white"
											>
												Update Details
											</button>

											<div className="mt-4">
												<p className="text-xs font-semibold uppercase tracking-[0.12em] text-orange-700">Host message</p>
												<div className="mt-2 flex flex-wrap gap-2">
													<button
														type="button"
														onClick={() => setHostMessageTarget('group')}
														className={`rounded-full border px-3 py-1 text-xs font-semibold ${hostMessageTarget === 'group' ? 'border-orange-300 bg-orange-100 text-orange-800' : 'border-gray-200 text-gray-600'}`}
													>
														Everyone
													</button>
													{hostTargets.map((member) => (
														<button
															key={`host-target-${member.id}`}
															type="button"
															onClick={() => setHostMessageTarget(String(member.id))}
															className={`rounded-full border px-3 py-1 text-xs font-semibold ${hostMessageTarget === String(member.id) ? 'border-orange-300 bg-orange-100 text-orange-800' : 'border-gray-200 text-gray-600'}`}
														>
															{member.name}
														</button>
													))}
												</div>
												<textarea
													rows={3}
													value={hostMessageText}
													onChange={(e) => setHostMessageText(e.target.value)}
													className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
													placeholder={hostMessageTarget === 'group' ? 'Message everyone at this table...' : 'Message this participant...'}
												/>
												<button
													type="button"
													onClick={sendHostMessage}
													disabled={sendingHostMessage}
													className="mt-2 w-full rounded-xl border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 disabled:opacity-70"
												>
													{sendingHostMessage ? 'Sending...' : 'Send host message'}
												</button>
											</div>
										</div>
									)}
								</aside>
							</div>

							{/* {selectedEvent.isJoined && (
								<div className="mt-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
									<div className="flex items-center justify-between gap-3">
										<h3 className="text-lg font-bold text-gray-900">Table chat</h3>
										<button type="button" onClick={() => loadGroupMessages(selectedEvent.id)} className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600">Refresh</button>
									</div>

									{groupChatLoading ? (
										<div className="mt-3 animate-pulse space-y-2"><div className="h-8 rounded bg-gray-100" /><div className="h-8 rounded bg-gray-100" /></div>
									) : groupMessages.length === 0 ? (
										<p className="mt-3 text-sm text-gray-500">No messages yet. Start the conversation.</p>
									) : (
										<div className="mt-3 max-h-72 space-y-2 overflow-auto pr-1">
											{groupMessages.slice(-40).map((msg) => (
												<div key={`table-msg-${msg.id}`} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
													<p className="text-xs font-semibold text-orange-700">
														{msg.sender?.name || 'Member'}{msg.recipientUserId ? ` -> ${msg.recipient?.name || 'Participant'}` : ' -> Everyone'}
													</p>
													<p className="mt-1 text-sm text-gray-700">{msg.text}</p>
												</div>
											))}
										</div>
									)}

									<div className="mt-3 flex flex-col gap-2 sm:flex-row">
										<textarea
											rows={2}
											value={groupMessageText}
											onChange={(e) => setGroupMessageText(e.target.value)}
											className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
											placeholder="Send a message to table chat"
										/>
										<button
											type="button"
											onClick={sendGroupMessage}
											disabled={sendingGroupMessage}
											className="rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-70"
										>
											{sendingGroupMessage ? 'Sending...' : 'Send'}
										</button>
									</div>
								</div>
							)} */}
						</>
					)}
				</div>
			)}
				{manageOpen && selectedEvent && (
						<div className="rounded-3xl border border-orange-200 bg-white p-5 shadow-xl sm:p-6">
							<div className="flex items-start justify-between gap-3">
								<div>
									<p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">Manage Table</p>
									<h3 className="title-font mt-1 text-xl font-bold text-gray-900">Update details + notify guests</h3>
								</div>
								<button
									type="button"
									onClick={() => { setManageOpen(false); setManageError('') }}
									className="rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
								>
									Close
								</button>
							</div>

							<form className="mt-5 grid gap-4 md:grid-cols-2" onSubmit={submitManage}>
								<label className="text-sm font-semibold text-gray-700">Table name
									<input value={manageForm.tableName} onChange={(e) => setManageForm((prev) => ({ ...prev, tableName: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Date & time
									<input type="datetime-local" value={manageForm.dateTime} onChange={(e) => setManageForm((prev) => ({ ...prev, dateTime: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Time zone
									<select value={manageForm.timeZone} onChange={(e) => setManageForm((prev) => ({ ...prev, timeZone: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required>
										{TIME_ZONE_OPTIONS.map((tz) => (
											<option key={tz.value} value={tz.value}>{tz.label}</option>
										))}
									</select>
								</label>
								<label className="text-sm font-semibold text-gray-700">Location
									<input value={manageForm.location} onChange={(e) => setManageForm((prev) => ({ ...prev, location: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Seats
									<input type="number" min="2" max="20" value={manageForm.seats} onChange={(e) => setManageForm((prev) => ({ ...prev, seats: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" required />
								</label>
								<label className="text-sm font-semibold text-gray-700">Cuisine emoji
									<input value={manageForm.cuisineEmoji} onChange={(e) => setManageForm((prev) => ({ ...prev, cuisineEmoji: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Cuisine label
									<input value={manageForm.cuisineLabel} onChange={(e) => setManageForm((prev) => ({ ...prev, cuisineLabel: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Duration (minutes)
									<input type="number" min="30" max="300" value={manageForm.durationMinutes} onChange={(e) => setManageForm((prev) => ({ ...prev, durationMinutes: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">City
									<input value={manageForm.city} onChange={(e) => setManageForm((prev) => ({ ...prev, city: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Area
									<input value={manageForm.area} onChange={(e) => setManageForm((prev) => ({ ...prev, area: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Tone
									<input value={manageForm.tone} onChange={(e) => setManageForm((prev) => ({ ...prev, tone: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Vibe
									<input value={manageForm.vibe} onChange={(e) => setManageForm((prev) => ({ ...prev, vibe: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700">Who it's for
									<input value={manageForm.forWho} onChange={(e) => setManageForm((prev) => ({ ...prev, forWho: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700 md:col-span-2">Discussion points (one per line)
									<textarea rows={4} value={manageForm.discussionPoints} onChange={(e) => setManageForm((prev) => ({ ...prev, discussionPoints: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>
								<label className="text-sm font-semibold text-gray-700 md:col-span-2">Table guidelines (one per line)
									<textarea rows={3} value={manageForm.guidelines} onChange={(e) => setManageForm((prev) => ({ ...prev, guidelines: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>

								{/* Pricing */}
								<div className="md:col-span-2 rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
									<p className="text-sm font-semibold text-orange-800">Table pricing</p>
									<div className="mt-3 flex flex-wrap gap-2">
										{[{id:'FREE',label:'Free'},{id:'SPLIT',label:'Split bill at table'},{id:'FIXED',label:'Fixed price per person'}].map((opt) => (
											<button
												key={opt.id}
												type="button"
												onClick={() => setManageForm((prev) => ({ ...prev, priceType: opt.id }))}
												className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${manageForm.priceType === opt.id ? 'border-orange-400 bg-orange-500 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'}`}
											>
												{opt.label}
											</button>
										))}
									</div>
									{manageForm.priceType === 'FIXED' && (
										<div className="mt-3 flex gap-3">
											<label className="flex-1 text-sm font-semibold text-gray-700">Amount per person
												<input type="number" min="1" step="0.01" value={manageForm.pricePerPerson} onChange={(e) => setManageForm((prev) => ({ ...prev, pricePerPerson: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="e.g. 500" required />
											</label>
											<label className="w-28 text-sm font-semibold text-gray-700">Currency
												<select value={manageForm.priceCurrency} onChange={(e) => setManageForm((prev) => ({ ...prev, priceCurrency: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm">
													<option value="INR">INR ₹</option>
													<option value="USD">USD $</option>
													<option value="AED">AED د.إ</option>
													<option value="SGD">SGD S$</option>
													<option value="GBP">GBP £</option>
													<option value="EUR">EUR €</option>
												</select>
											</label>
										</div>
									)}
									{manageForm.priceType === 'SPLIT' && (
										<p className="mt-2 text-xs text-orange-700">Guests will see a note that the bill will be split equally at the table.</p>
									)}
								</div>

								<label className="text-sm font-semibold text-gray-700 md:col-span-2">Notification message to participants
									<textarea rows={3} value={notifyMessage} onChange={(e) => setNotifyMessage(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
								</label>

								{manageError && <p className="md:col-span-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{manageError}</p>}

								<div className="md:col-span-2 flex justify-end gap-2">
									<button type="button" onClick={() => setManageOpen(false)} className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600">Cancel</button>
									<button type="submit" disabled={managing} className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70">
										{managing ? 'Saving...' : 'Save and notify'}
									</button>
								</div>
							</form>
						</div>
					)}
		</section>
	)
}
